/**
 * Synthetic placeholder representing a missing time bucket in an aggregated
 * series. Carries only the time range (Unix-epoch nanoseconds); no value
 * field. Use the `"value" in x` type guard at the use site to distinguish
 * real records from placeholders.
 */
export interface BucketPlaceholder {
  start_time: bigint;
  end_time: bigint;
}

/**
 * Insert synthetic placeholder entries for any missing time buckets between
 * consecutive real records.
 *
 * Gap size is measured against each record's own duration, so no period
 * arithmetic is needed — the function works for daily/weekly/monthly/quarterly
 * /yearly aggregates whose individual buckets have stable durations (within
 * the gap they bracket). All values are Unix-epoch nanoseconds.
 */
export function fillMissingBuckets<T extends BucketPlaceholder>(
  data: T[],
): (T | BucketPlaceholder)[] {
  if (data.length < 2) {
    return data;
  }

  // `start_time` is bigint; Array.sort needs a number comparator, and only the
  // sign matters — Number() of the difference preserves it (cf. computeTrend).
  const sorted = [...data].sort((a, b) => Number(a.start_time - b.start_time));

  const result: (T | BucketPlaceholder)[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];

    const prevDurationNs = prev.end_time - prev.start_time;
    const gapNs = curr.start_time - prev.end_time;

    // Round to nearest whole bucket: bucket durations vary slightly (e.g.
    // month lengths), so a truncating bigint divide would mis-count. The
    // ratio is a small integer, so the Number() conversion is exact here.
    const missingCount = Math.round(Number(gapNs) / Number(prevDurationNs));
    for (let j = 0; j < missingCount; j++) {
      const bucketStartNs = prev.end_time + BigInt(j) * prevDurationNs;
      result.push({
        start_time: bucketStartNs,
        end_time: bucketStartNs + prevDurationNs,
      });
    }

    result.push(curr);
  }

  return result;
}
